import React, { useState, useEffect, useRef } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import styled from 'styled-components';

import { Table, Loading, NoResults, ButtonPrimary, Icon } from '../../common/components';
import { respondTo } from '../../common/styles/mixins';
import { FAQManagementGridWrapper } from './FAQManagementStyles';
import moment from 'moment';
import { DATE_FORMAT } from '../../common/constants';

import iconSet from '../../assets/selection.json';
import IcomoonReact from 'icomoon-react';
import * as colors from '../../common/styles/colors';

const StyledTableWrapper = styled.div`
  ${respondTo.tablet`
    padding-right: 33px;
  `};
`;

type FAQManagementGridComponentProps = {
  selectedRowIndex: number;
  gridData: Api.IDocumentRepositoryRequest[];
  handleSortChange: Function;
  handlePagingChange: Function;
  handleSelectedRowChange: Function;
  handleDelete: Function;
  isLoading?: boolean;
  hasMoreData: boolean;
  currentSortField: string;
  currentSortOrder: 'ASC' | 'DESC' | '';
};

const defaultProps = {
  isLoading: true,
  gridData: [],
  selectedRowIndex: -1,
  hasMoreData: true,
  currentSortField: '',
  currentSortOrder: 'ASC',
};

const actionStyles = {
  width: '10px',
};

const adminHeaders: Array<Common.HeaderItem> = [
  {
    title: 'Id',
    dataValue: 'faqId',
    enableSort: false,
  },
  {
    title: 'Category',
    dataValue: 'faqCategory',
    enableSort: false,
  },
  {
    title: 'Question',
    dataValue: 'question',
    enableSort: false,
    className: 'd-none d-md-table-cell question-col',
  },
  {
    title: 'Audience',
    dataValue: 'faqAudience',
    enableSort: false,
  },
  {
    title: 'Priority',
    dataValue: 'priority',
    enableSort: false,
  },
  {
    title: 'Created date',
    dataValue: 'createdDate',
    enableSort: true,
  },
  {
    title: 'Updated by',
    dataValue: 'updatedBy',
    enableSort: false,
  },
  {
    title: 'Status',
    dataValue: 'statusExt',
    enableSort: false,
  },
];

export const FAQManagementGrid = (props: FAQManagementGridComponentProps) => {
  const { gridData, selectedRowIndex, isLoading } = props;
  const [selectedRow, setSelectedRow] = useState<number | null>(null);
  const sortDetails: Common.ISort = { field: props.currentSortField, order: props.currentSortOrder };
  const scrollableTable = useRef<any>();

  useEffect(() => {
    if (selectedRowIndex < 0) {
      clearSelection();
    }
  }, [selectedRowIndex]);

  const clearSelection = () => {
    setSelectedRow(null);
  };

  const handleRowSelect = (event: React.MouseEvent<HTMLAnchorElement>, row: number | null) => {
    if (row !== null) {
      setSelectedRow(row);
      props.handleSelectedRowChange(row, gridData[row]);
    }
  };

  const handleDeleteClick = (event: React.MouseEvent<HTMLAnchorElement>, row: any) => {
    event.preventDefault();
    props.handleDelete(row);
  };

  const getActions = (row: any) => {
    return (
      <IcomoonReact
        className='icon-trash'
        color={colors.oxfordBlue}
        iconSet={iconSet}
        size={20}
        icon='trash2'
        onClick={(e: React.MouseEvent<HTMLAnchorElement>) => handleDeleteClick(e, row)}
      />
    );
  };

  const handleSort = (event: React.MouseEvent<HTMLAnchorElement>) => {
    clearSelection();
    const typeId = event.currentTarget.dataset.value || '';
    const details = sortDetails;

    if (details.field === typeId) {
      details.order = details.order === 'ASC' ? 'DESC' : 'ASC';
    } else {
      details.field = typeId;
      details.order = 'ASC';
    }

    props.handleSortChange(details);
    event.preventDefault();
  };

  const handlePagingChange = () => {
    props.handlePagingChange();
  };

  const renderDate = (value: any) => {
    if (value) {
      return <td>{moment(value).format(DATE_FORMAT)}</td>;
    } else {
      return <td></td>;
    }
  };

  return (
    <FAQManagementGridWrapper>
      {!isLoading && gridData.length === 0 && <NoResults />}

      {gridData.length > 0 && (
        <>
          <InfiniteScroll
            dataLength={gridData.length}
            next={handlePagingChange}
            hasMore={props.hasMoreData}
            loader={''}
            scrollableTarget='scrollableDiv'
            scrollThreshold={0.9}
          >
            <StyledTableWrapper
              id='scrollableDiv'
              ref={scrollableTable}
              className='table-responsive table-fixed fixed-column columns-1'
            >
              <Table
                headers={adminHeaders}
                actions={getActions}
                rows={gridData}
                onClickSort={handleSort}
                sortDetails={sortDetails}
                actionStyles={actionStyles}
                selectedRowIndex={selectedRow}
                handleSelectRow={handleRowSelect}
                className='list table plain text-left'
                rendercreatedDate={renderDate}
              />
            </StyledTableWrapper>
          </InfiniteScroll>
        </>
      )}

      {isLoading && <Loading overlay />}
    </FAQManagementGridWrapper>
  );
};

FAQManagementGrid.defaultProps = defaultProps;
